// app/frontend/pages/ArtistPortal.tsx
import React from 'react';
import { Head } from '@inertiajs/react';
import { Container, Title, Text, Accordion, Box, Stack, Divider, Button, Group, Anchor } from '@mantine/core';
import Layout from '../../components/Layout';
import '../../styles/PostShow.css';
import { notifications, Notifications } from '@mantine/notifications';

interface ArtistPortalProps {
  artist?: any;
  canEdit?: boolean;
}

type NextPageWithLayout = React.FC<ArtistPortalProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const ArtistPortal: NextPageWithLayout = ({ artist, canEdit = false }) => {
  console.log('ArtistPortal props:', { artist, canEdit });

  if (!artist) {
    return (
      <Container size="sm" py="xl">
        <Text className="text-white" ta="center">
          Loading artist portal...
        </Text>
      </Container>
    );
  }

  const portalSections = [
    {
      section: '📝 Bio',
      items: [
        {
          question: 'Short Bio',
          answer: artist?.short_bio || 'Not specified',
        },
        {
          question: 'Long Bio',
          answer: artist?.description || 'Not specified',
        },
      ],
    },
    {
      section: '📸 Promo',
      items: [
        {
          question: 'Photos',
          answer: artist?.promo_photos || 'Not specified',
        },
        {
          question: 'Videos',
          answer: artist?.promo_videos || 'Not specified',
        },
      ],
    },
    {
      section: '✈️ Travel',
      items: [
        {
          question: 'Traveler Info',
          answer: artist?.traveler_info || 'Not specified',
        },
        {
          question: 'Preferred Airports',
          answer: artist?.preferred_airports || 'Not specified',
        },
      ],
    },
    {
      section: '🛏️ Accommodation',
      items: [
        {
          question: 'My Rooming Preferences',
          answer: artist?.accommodation_info || 'Not specified',
        },
      ],
    },
    {
      section: '🍽️ Diet',
      items: [
        {
          question: 'Foods I Dislike/Have Allergies To',
          answer: artist?.disliked_foods || 'Not specified',
        },
        {
          question: 'Foods I Love',
          answer: artist?.favorite_foods || 'Not specified',
        },
      ],
    },
    {
      section: '💸 Payment',
      items: [
        {
          question: 'My Artist Rates',
          answer: artist?.rates || 'Not specified',
        },
        {
          question: 'My Payment Methods',
          answer: artist?.payment_methods || 'Not specified',
        },
      ],
    },
  ];

  return (
    <>
      <Head>
        <title>Artist Portal | Where Can We Dance</title>
      </Head>

      <Notifications position="top-right" />

      <Container size="sm" py="xl">
        <Stack gap="lg">
          <Title order={1} fw={700} ta="center" className="text-white rubik-header">
            {artist.name} Booking Portal
          </Title>

          {/* Navigation Buttons */}
          <Group justify="center" gap="md">
            {canEdit && artist?.slug && (
              <Anchor href={`/artists/${artist.slug}/edit_portal_info`} style={{ textDecoration: 'none' }}>
                <Button variant="outline" color="#EA580C" fw={500}>
                  Edit Portal Info
                </Button>
              </Anchor>
            )}
            {artist?.slug && (
              <Anchor href={`/artists/${artist.slug}`} style={{ textDecoration: 'none' }}>
                <Button variant="filled" color="#EA580C" fw={500}>
                  View Artist Page
                </Button>
              </Anchor>
            )}
          </Group>

          <Divider my="md" />

          {/* Portal Sections */}
          {portalSections.map((section, sectionIndex) => (
            <Box key={sectionIndex} mb="lg">
              <Title order={2} mb="md" fw={600} className="text-white poppins">
                {section.section}
              </Title>

              <Accordion
                multiple
                defaultValue={[]}
                classNames={{
                  control: 'faq-accordion-control',
                  content: 'faq-accordion-content',
                  item: 'faq-accordion-item',
                  chevron: 'faq-accordion-chevron',
                }}
              >
                {section.items.map((item, itemIndex) => (
                  <Accordion.Item key={itemIndex} value={`${sectionIndex}-${itemIndex}`}>
                    <Accordion.Control>{item.question}</Accordion.Control>
                    <Accordion.Panel>
                      {item.answer && item.answer !== 'Not specified' && item.answer.includes('<') ? (
                        <div className="text-white rich-text-post" dangerouslySetInnerHTML={{ __html: item.answer }} />
                      ) : (
                        <Text className="text-white">{item.answer}</Text>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Box>
          ))}
        </Stack>
      </Container>
    </>
  );
};

ArtistPortal.layout = (page: React.ReactNode) => <Layout>{page}</Layout>;

export default ArtistPortal;
